import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, GripVertical, Lightbulb, Loader2, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { bitEvent } from "@/lib/bit";
import { Shell } from "@/lib/terminalSim";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = {
  parsons: "Code-Puzzle",
  predict: "Vorhersage",
  choice: "Szenario",
  bughunt: "Bug-Jagd",
  fillblank: "Lückentext",
  terminal: "Terminal",
};

const codeBox = "rounded-xl border border-line bg-[#070A12] font-mono text-[15px] leading-relaxed text-foreground";

/* ------------------------------------------------------------------ types */

function Parsons({ ex, value, onChange }) {
  const lines = value ?? ex.lines;
  const dragFrom = useRef(null);
  const move = (from, to) => {
    if (to < 0 || to >= lines.length || from === to) return;
    const next = lines.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  return (
    <ol className="space-y-2" aria-label="Code-Zeilen, per Ziehen oder Pfeiltasten sortierbar">
      {lines.map((l, i) => (
        <li
          key={l.id}
          draggable
          onDragStart={() => (dragFrom.current = i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => move(dragFrom.current, i)}
          className="flex items-center gap-2 rounded-xl border border-line bg-[#070A12] py-1.5 pl-2 pr-1.5 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} aria-hidden="true" />
          <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre font-mono text-[15px] text-foreground" style={{ paddingLeft: l.indent * 28 }}>
            {l.code}
          </code>
          <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`„${l.code}“ nach oben`} className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-muted-foreground hover:text-foreground disabled:opacity-30">
            <ArrowUp className="h-4 w-4" strokeWidth={2.2} />
          </button>
          <button type="button" onClick={() => move(i, i + 1)} disabled={i === lines.length - 1} aria-label={`„${l.code}“ nach unten`} className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-muted-foreground hover:text-foreground disabled:opacity-30">
            <ArrowDown className="h-4 w-4" strokeWidth={2.2} />
          </button>
        </li>
      ))}
    </ol>
  );
}

function Predict({ ex, value, onChange }) {
  return (
    <div className="space-y-4">
      {ex.code && <pre className={`${codeBox} overflow-x-auto whitespace-pre-wrap p-4`}>{ex.code}</pre>}
      <div role="radiogroup" aria-label="Antworten" className="grid gap-2 sm:grid-cols-2">
        {ex.options.map((opt, i) => {
          const sel = value === i;
          return (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={sel}
              onClick={() => onChange(i)}
              className={`min-h-[52px] rounded-xl border px-4 py-3 text-left text-[15px] transition-colors ${ex.type === "choice" ? "" : "font-mono "}${
                sel ? "border-primary bg-primary/10 text-foreground" : "border-input bg-[#070A12] text-foreground hover:border-primary/50"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BugHunt({ ex, value, onChange }) {
  return (
    <div className={`${codeBox} overflow-x-auto p-2`}>
      {ex.code_lines.map((line, i) => {
        const sel = value === i;
        return (
          <button
            key={i}
            type="button"
            aria-pressed={sel}
            onClick={() => onChange(i)}
            className={`flex w-full min-h-[40px] items-center rounded-lg border px-2 text-left whitespace-pre transition-colors ${
              sel ? "border-secondary bg-secondary/10" : "border-transparent hover:bg-muted"
            }`}
          >
            <span className="w-8 shrink-0 text-muted-foreground">{i + 1}</span>
            {line}
          </button>
        );
      })}
    </div>
  );
}

function FillBlank({ ex, value, onChange, wrong }) {
  const answers = value ?? Array(ex.blanks).fill("");
  const parts = ex.template.split("___");
  return (
    <div className={`${codeBox} overflow-x-auto whitespace-pre p-4`}>
      {parts.map((part, i) => (
        <span key={i}>
          {part}
          {i < parts.length - 1 && (
            <input
              value={answers[i]}
              onChange={(e) => {
                const next = answers.slice();
                next[i] = e.target.value;
                onChange(next);
              }}
              aria-label={`Lücke ${i + 1}`}
              spellCheck={false}
              autoComplete="off"
              size={Math.max(6, answers[i].length + 1)}
              className={`mx-0.5 rounded-md border bg-background px-1.5 py-0.5 font-mono text-primary focus:outline-none focus:ring-2 focus:ring-primary ${
                wrong?.includes(i) ? "border-destructive" : "border-input"
              }`}
            />
          )}
        </span>
      ))}
    </div>
  );
}

function Terminal({ ex, value, onChange }) {
  const [shell, setShell] = useState(() => new Shell(ex.setup));
  const [lines, setLines] = useState([{ kind: "out", text: "Tippe help für alle Befehle." }]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const history = value ?? [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  const run = () => {
    const cmd = input.trim();
    setInput("");
    if (!cmd) return;
    const prompt = shell.prompt;
    const out = shell.run(cmd);
    setLines((prev) => (out.some((o) => o.kind === "clear") ? [] : [...prev, { kind: "cmd", text: prompt + cmd }, ...out]).slice(-80));
    setShell(shell);
    onChange([...history, cmd].slice(-60));
  };

  const reset = () => {
    setShell(new Shell(ex.setup));
    setLines([{ kind: "out", text: "Neu gestartet. Tippe help für alle Befehle." }]);
    onChange([]);
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-xl border border-line bg-[#05070D] font-mono text-sm" onClick={() => inputRef.current?.focus()}>
        <div className="flex items-center gap-1.5 border-b border-line bg-[#0B1020] px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-secondary" />
          <span className="h-2.5 w-2.5 rounded-full bg-warning" />
          <span className="h-2.5 w-2.5 rounded-full bg-accent" />
          <span className="ml-2 text-xs text-muted-foreground">lerner@devpath – bash (Simulation)</span>
          <button type="button" onClick={reset} className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.2} /> Neu starten
          </button>
        </div>
        <div ref={scrollRef} className="h-56 overflow-y-auto px-3 py-2 leading-relaxed">
          {lines.map((l, i) => (
            <div key={i} className={`whitespace-pre-wrap break-words ${l.kind === "err" ? "text-red-300" : l.kind === "cmd" ? "text-foreground" : "text-muted-foreground"}`}>
              {l.text}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <label htmlFor={`term-${ex.id}`} className="whitespace-nowrap text-primary">{shell.prompt}</label>
            <input
              id={`term-${ex.id}`}
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  run();
                }
              }}
              autoComplete="off"
              spellCheck={false}
              maxLength={300}
              className="min-w-0 flex-1 border-none bg-transparent p-0 text-foreground focus:outline-none focus:ring-0"
            />
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Ziel: <span className="text-foreground">{ex.goal_text}</span> · {history.length} Befehl(e) ausgeführt
      </p>
    </div>
  );
}

const RENDERERS = { parsons: Parsons, predict: Predict, choice: Predict, bughunt: BugHunt, fillblank: FillBlank, terminal: Terminal };

/* ------------------------------------------------------------------ card */

function answerFor(ex, value) {
  if (ex.type === "parsons") return (value ?? ex.lines).map((l) => l.id);
  if (ex.type === "fillblank") return value ?? Array(ex.blanks).fill("");
  if (ex.type === "terminal") return value ?? [];
  return value;
}

function Exercise({ ex, onSolved }) {
  const { refreshUser } = useAuth();
  const [value, setValue] = useState(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(ex.done ? { passed: true, message: "Bereits gelöst." } : null);
  const [hints, setHints] = useState(ex.hints_shown);
  const [hinting, setHinting] = useState(false);
  const Renderer = RENDERERS[ex.type];

  const ready = ["predict", "choice", "bughunt"].includes(ex.type) ? value !== null : ex.type === "terminal" ? (value ?? []).length > 0 : true;

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`/exercises/${ex.id}/check`, { answer: answerFor(ex, value) });
      setResult(data);
      if (data.passed) {
        bitEvent("task-solved", {
          task: { instructions: ex.prompt || ex.title },
          lessonTitle: ex.title,
          text: data.xp_awarded > 0 ? "Übung gelöst!" : "Richtig – die hattest du schon.",
          xp: data.xp_awarded,
        });
        onSolved(ex.id);
        if (data.xp_awarded > 0) refreshUser();
      } else {
        bitEvent("fail", { text: data.message, context: `Übung „${ex.title}“: ${ex.prompt}` });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setChecking(false);
    }
  };

  const hint = async () => {
    setHinting(true);
    try {
      const { data } = await api.post(`/exercises/${ex.id}/hint`);
      if (data.hint) {
        setHints((h) => [...h, data.hint]);
        bitEvent("hint", { text: `Tipp ${data.level} von ${data.hints_total}: ${data.hint}` });
        if (data.xp_spent) refreshUser();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setHinting(false);
    }
  };

  const hintsLeft = ex.hints_total - hints.length;

  return (
    <div>
      <h3 className="font-heading text-xl font-bold text-foreground">{ex.title}</h3>
      <p className="mb-4 mt-1 text-muted-foreground">{ex.prompt}</p>
      <Renderer ex={ex} value={value} onChange={(v) => { setValue(v); if (result && !result.passed) setResult(null); }} wrong={result?.wrong_blanks} />

      {hints.length > 0 && (
        <ul className="mt-4 space-y-2">
          {hints.map((h, i) => (
            <li key={i} className="flex gap-2 rounded-xl border border-secondary/40 bg-secondary/10 px-3 py-2 text-sm text-[#FDF2F8]">
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-secondary" strokeWidth={2.2} />
              <span><strong className="text-secondary">Tipp {i + 1}:</strong> {h}</span>
            </li>
          ))}
        </ul>
      )}

      {result && (
        <div
          role="status"
          className={`mt-4 rounded-xl border px-4 py-3 text-sm ${result.passed ? "border-accent/60 bg-accent/10 text-accent" : "border-destructive/60 bg-destructive/10 text-red-300"}`}
        >
          <p className="font-semibold">
            {result.message}
            {result.xp_awarded > 0 && ` +${result.xp_awarded} XP`}
          </p>
          {result.explanation && <p className="mt-1 text-foreground/90">{result.explanation}</p>}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" onClick={check} disabled={checking || !ready} className="flex min-h-[48px] items-center gap-2 rounded-xl bg-primary px-5 font-semibold disabled:opacity-50">
          {checking ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} /> : <Check className="h-4 w-4" strokeWidth={2.4} />}
          Prüfen
        </button>
        <button
          type="button"
          onClick={hint}
          disabled={hinting || hintsLeft === 0}
          className="flex min-h-[48px] items-center gap-2 rounded-xl border border-secondary/60 px-5 font-semibold text-[#FBCFE8] hover:bg-secondary/10 disabled:opacity-50"
        >
          <Lightbulb className="h-4 w-4" strokeWidth={2.2} />
          Tipp von Bit
          <span className="text-sm font-normal text-muted-foreground">
            {hintsLeft > 0 ? `(${ex.done ? "gratis" : "−2 XP"} · ${hintsLeft} übrig)` : "(keine mehr)"}
          </span>
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ deck */

export default function ExerciseDeck({ slug, lessonId }) {
  const [exercises, setExercises] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setExercises(null);
    setActive(0);
    api
      .get(`/lessons/${slug}/${lessonId}/exercises`)
      .then((r) => setExercises(r.data))
      .catch(() => setExercises([]));
  }, [slug, lessonId]);

  if (!exercises || exercises.length === 0) return null;

  const solved = exercises.filter((e) => e.done).length;
  const markSolved = (id) => setExercises((list) => list.map((e) => (e.id === id ? { ...e, done: true } : e)));
  const ex = exercises[active];

  return (
    <section className="nb-card p-5 sm:p-6" aria-labelledby="exercises-heading" data-testid="exercise-deck">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">// Üben</p>
          <h2 id="exercises-heading" className="font-heading text-2xl font-bold text-foreground">Interaktive Übungen</h2>
        </div>
        <div className="flex items-center gap-1.5" aria-label={`${solved} von ${exercises.length} Übungen gelöst`}>
          {exercises.map((e) => (
            <span key={e.id} className={`h-1.5 w-7 rounded-full ${e.done ? "trace-on" : "bg-line"}`} />
          ))}
          <span className="ml-2 font-mono text-sm text-muted-foreground">{solved}/{exercises.length}</span>
        </div>
      </div>

      <div role="tablist" aria-label="Übungen" className="mb-5 flex flex-wrap gap-2">
        {exercises.map((e, i) => (
          <button
            key={e.id}
            role="tab"
            aria-selected={i === active}
            onClick={() => setActive(i)}
            className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-3.5 text-sm font-semibold transition-colors ${
              i === active ? "border-primary bg-primary/10 text-foreground" : "border-line text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${e.done ? "bg-accent shadow-[0_0_8px_hsl(var(--accent))]" : "bg-input"}`} />
            {TYPE_LABEL[e.type]}
          </button>
        ))}
      </div>

      <Exercise key={ex.id} ex={ex} onSolved={markSolved} />
    </section>
  );
}
